import type { Horario } from '~~/interfaces';
import { formatDate } from '~~/helpers/hora';
import capitalize from 'lodash/capitalize';
import deburr from 'lodash/deburr';

export const DAYS = {
  Lu: 'L-V',
  Ma: 'L-V',
  Mi: 'L-V',
  Ju: 'L-V',
  Vi: 'L-V',
  Sa: 'S',
  Do: 'D',
} as const;

export type DAYS_KEYS = keyof typeof DAYS;

const getCurrentDayType = (date: Date): DAYS_KEYS => {
  return deburr(
    capitalize(
      date
        .toLocaleDateString('es-CL', {
          weekday: 'short',
        })
        .slice(0, 2)
    )
  ) as DAYS_KEYS;
};

const formatTime = (time: string): string => {
  if (!time) return '00:00';
  if (!/^\d{1,2}:\d{2}/.test(time)) return '00:00';
  return time.replace(/^(\d{2}):(\d{2}):\d{2}/gi, '$1:$2');
};

const getRelativeTime = (time: string): string => {
  if (!time) return '00:00';
  if (!/^\d{1,2}:\d{2}/.test(time)) return '00:00';

  const [h, m] = time.split(':');
  const now = new Date();
  const targetDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    Number.parseInt(h || '0', 10),
    Number.parseInt(m || '0', 10)
  );

  const formattedTime = formatDate(targetDate);
  if (!formattedTime) return '00:00';

  return formattedTime.replace(
    /(.+) (\d+) (h|m)?/gi,
    (str: string, $1: string, $2: string, $3: string) => {
      const ext = $3
        ? $3.replace(/horas?/, 'hr').replace(/minutos?/, 'min')
        : '';
      const prefix = $1 ? $1.replace('dentro de', 'en').trim() : '';
      return `${prefix} ${$2} ${ext}`;
    }
  );
};

const isValidSchedule = (schedule: Horario) => {
  if (!schedule?.S || typeof schedule.S !== 'string') return false;

  if (!Array.isArray(schedule.V) || schedule.V.length === 0) return false;

  if (!/^\d{1,2}:\d{2}/.test(schedule.S)) return false;

  const [hours] = schedule.S.split(':').map(Number);

  if (hours && hours >= 24) return false;
  return true;
};

const separateHourAndMinute = (time: string): number[] => {
  if (!time) return [];
  if (!/^\d{1,2}:\d{2}/.test(time)) return [];
  return time.split(':').map(Number) || [];
};

export const useScheduleFiltering = () => {
  const filterSchedules = (
    schedules: Horario[],
    currentTime: string,
    dayType: DAYS_KEYS,
    limit = 0,
    isFeriado = false
  ): Horario[] => {
    if (!schedules || !Array.isArray(schedules) || !currentTime) return [];

    // Sort schedules by time
    const BASE_DATE = new Date().toISOString().split('T')[0];

    // Mark first valid schedule and process
    let firstValidFound = false;
    let firstValidHighlighted = false;

    const processedSchedules = [...schedules]
      .sort((a, b) => {
        const timeA = new Date(`${BASE_DATE}T${a.S}`).getTime();
        const timeB = new Date(`${BASE_DATE}T${b.S}`).getTime();

        if (Number.isNaN(timeA) || Number.isNaN(timeB)) return 0;
        return timeA - timeB;
      })
      .reduce((init, schedule, iSchedule, schedules) => {
        if (!isValidSchedule(schedule)) return init;

        const newSchedule: Horario[] = [];

        const [currentHour = 0, currentMinute = 0] =
          separateHourAndMinute(currentTime);
        const [scheduleHour = 0, scheduleMinute = 0] = separateHourAndMinute(
          schedule.S
        );

        // Compare hours first
        let isScheduleVigente = false;

        if (!isFeriado && schedule.V.includes(DAYS[dayType])) {
          if (
            scheduleHour > currentHour ||
            (scheduleHour === currentHour && scheduleMinute >= currentMinute)
          ) {
            isScheduleVigente = true;
            if (!firstValidFound) {
              firstValidFound = true;
            }
          }
        }

        let isFirstObject: Record<string, boolean> = {};

        if (firstValidFound && !firstValidHighlighted) {
          firstValidHighlighted = true;
          isFirstObject = { primero: true };
        }

        if (limit > 0 && !isScheduleVigente) return init;

        // ultimo horario no vigente (el que ya pasó)
        // checkea si el actual es primero, y el anterior es activo
        if (isFirstObject.primero) {
          for (let oldI = iSchedule - 1; oldI >= 0; oldI--) {
            const oldSchedule = schedules[oldI];
            if (!oldSchedule?.id) continue;

            const hasOldSchedule = init.findIndex(
              (sched) => sched.T === oldSchedule.T && sched.S === oldSchedule.S
            );

            if (oldSchedule?.V.includes(DAYS[dayType])) {
              if (hasOldSchedule >= 0 && init[hasOldSchedule]) {
                init[hasOldSchedule].old = true;
              } else {
                newSchedule.push({
                  ...oldSchedule,
                  old: true,
                });
              }
              break;
            }
          }
        }

        newSchedule.push({
          ...schedule,
          ...isFirstObject,
          vigente: isScheduleVigente,
        });

        return [...init, ...newSchedule];
      }, [] as Horario[]);

    return limit > 0 ? processedSchedules.slice(0, limit) : processedSchedules;
  };

  return {
    getCurrentDayType,
    formatTime,
    getRelativeTime,
    filterSchedules,
  };
};
